/**
 * Batch-12b — Jahresübersicht als EINE gemeinsame Komponente.
 *
 * Vorher existierten zwei unterschiedliche Blöcke (PbDashboard und
 * BetreuungenTab) um die geteilte CareYearTimeline herum. Diese Karte
 * übernimmt die Dashboard-Variante (Header, + Abwesenheit, + Betreuung,
 * Aufgaben-Marker) und integriert zusätzlich das Verordnungsmanagement
 * (Button + Drawer + blaue Termin-Marker). Beide Stellen rendern jetzt
 * ausschließlich diese Komponente.
 *
 * Die Karte lädt ihre Daten selbst (Cares, Abwesenheiten, Aufgaben,
 * Verordnungen) und kapselt die Edit-Drawer (Betreuung, Abwesenheit,
 * Aufgabe, Verordnungen) sowie das Anlegen neuer Betreuungen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { EditDrawer } from '../../components/ui';
import { Icon } from '../../components/icons';
import {
  v1Users, v1Absences, v2Verordnungen,
  type V1User, type V1Absence, type Verordnung,
} from '../../lib/v1';
import { CareYearTimeline, type CareRow, type TimelineTask } from './CareYearTimeline';
import { CareEditForm } from './profile/CareEditForm';
import { CreateCareModal } from './tabs/BetreuungenTab/CreateCareModal';
import { AbsenceForm } from './tabs/BetreuungenTab/AbsenceForm';
import { VerordnungenPanel } from './tabs/BetreuungenTab/VerordnungenPanel';
import { TaskEditForm } from './PbDashboard/AufgabenWidget';
import { useActiveAdmins } from './PbDashboard/helpers';
import type { TaskRow } from './PbDashboard/types';

export interface JahresuebersichtCardProps {
  user: V1User | null;
  dark: boolean;
  /**
   * Erzwingt ein Neu-Laden aller Daten, wenn der Wert sich ändert
   * (z.B. nachdem der Parent außerhalb der Karte eine Betreuung/Aufgabe
   * geändert hat).
   */
  reloadSignal?: number;
  /**
   * Nach jedem Cares-Load — der PbDashboard spiegelt die Liste in sein
   * BetreuungenWidget, statt sie doppelt zu laden.
   */
  onCaresLoaded?: (cares: CareRow[], loading: boolean) => void;
  /** Nach gespeicherten Änderungen an Cares/Abwesenheiten/Verordnungen. */
  onChanged?: () => void;
  /** PB-Startdatum wurde beim Anlegen einer Betreuung übernommen. */
  onPbStartdatumChanged?: (startdatum: string) => void;
  /** Nach gespeicherten Aufgaben-Änderungen (Task-Marker-Klick → Edit). */
  onTasksChanged?: () => void;
  /**
   * Ein-/Ausklappbar (Chevron im Kopf). Der Zustand wird je Einsatzort unter
   * `collapseKey` in localStorage gemerkt; Default ist ausgeklappt.
   */
  collapsible?: boolean;
  collapseKey?: string;
}

/** localStorage-Präfix für den Klapp-Zustand der Jahresübersicht. */
export const JAHRESUEBERSICHT_COLLAPSED_KEY = 'sh24.jahresuebersicht.collapsed';

export function JahresuebersichtCard({
  user, dark, reloadSignal = 0, onCaresLoaded, onChanged, onTasksChanged, onPbStartdatumChanged,
  collapsible = false, collapseKey = 'default',
}: JahresuebersichtCardProps) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  const admins = useActiveAdmins();

  const storageKey = `${JAHRESUEBERSICHT_COLLAPSED_KEY}.${collapseKey}`;
  const [collapsedState, setCollapsed] = useState<boolean>(() => {
    if (!collapsible) return false;
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const collapsed = collapsible && collapsedState;
  const toggleCollapsed = () => setCollapsed(c => {
    const next = !c;
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  const [cares, setCares] = useState<CareRow[]>([]);
  const [caresLoading, setCaresLoading] = useState(true);
  const [absences, setAbsences] = useState<V1Absence[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [verordnungen, setVerordnungen] = useState<Verordnung[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());

  // Drawer-/Modal-State
  const [editCare, setEditCare] = useState<CareRow | null>(null);
  const [absenceDraft, setAbsenceDraft] = useState<V1Absence | 'new' | null>(null);
  const [careCreateOpen, setCareCreateOpen] = useState(false);
  const [verordnungenOpen, setVerordnungenOpen] = useState(false);
  const [verordnungEditId, setVerordnungEditId] = useState<number | null>(null);
  const [editTask, setEditTask] = useState<TaskRow | null>(null);

  const loadCares = useCallback(() => {
    if (!user?.id) return;
    setCaresLoading(true);
    onCaresLoaded?.([], true);
    v1Users.cares(user.id, { perPage: 100, filters: { showOldCares: 'true' } })
      .then(p => {
        const rows = (p.data ?? []) as CareRow[];
        setCares(rows);
        onCaresLoaded?.(rows, false);
      })
      .catch(() => { onCaresLoaded?.([], false); })
      .finally(() => setCaresLoading(false));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAbsences = useCallback(() => {
    if (!user?.id) return;
    v1Absences.listForUser(user.id).then(setAbsences).catch(() => {/* Timeline degradiert zu leer */});
  }, [user?.id]);

  const loadTasks = useCallback(() => {
    if (!user?.id) return;
    v1Users.tasks(user.id, { perPage: 200, filters: { showFinishedTasks: 'true' } })
      .then(p => setTasks(p.data as unknown as TaskRow[]))
      .catch(() => {/* Timeline degradiert zu leer */});
  }, [user?.id]);

  const loadVerordnungen = useCallback(() => {
    if (!user?.id) return;
    v2Verordnungen.listForUser(user.id).then(setVerordnungen).catch(() => {/* degradiert zu leer */});
  }, [user?.id]);

  useEffect(() => { loadCares(); }, [loadCares, reloadSignal]);
  useEffect(() => { loadAbsences(); }, [loadAbsences, reloadSignal]);
  useEffect(() => { loadTasks(); }, [loadTasks, reloadSignal]);
  useEffect(() => { loadVerordnungen(); }, [loadVerordnungen, reloadSignal]);

  if (!user?.id) return null;

  // BK-Profil: Verfügbarkeit (Dispo) statt PB-Abwesenheiten; Verordnungen
  // gibt es nur für Pflegebedürftige.
  const isBk = user.user_role === 1;

  return (
    <>
      {/* ── Jahresübersicht — volle Breite (Dashboard-Layout) ─────────── */}
      <div style={{ background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: collapsed ? '20px 24px' : '20px 24px 0' }}>
          {collapsible ? (
            <button
              type="button"
              data-testid="jahresuebersicht-toggle"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={t(collapsed ? 'pflege.cares.timeline_expand' : 'pflege.cares.timeline_collapse')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }}
            >
              <span style={{ marginTop: 2 }}><Icon.Chevron dir={collapsed ? 'right' : 'down'} c={tc.muted} /></span>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: tc.heading }}>{t('pflege.detail.dashboard.jahresuebersicht')}</span>
                <span style={{ display: 'block', fontSize: 12, color: tc.muted, marginTop: 2 }}>{t('pflege.cares.timeline_subtitle')}</span>
              </span>
            </button>
          ) : (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: tc.heading }}>{t('pflege.detail.dashboard.jahresuebersicht')}</div>
            <div style={{ fontSize: 12, color: tc.muted, marginTop: 2 }}>{t('pflege.cares.timeline_subtitle')}</div>
          </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Batch-12b: Verordnungsmanagement in der gemeinsamen Karte */}
            {!isBk && (
              <button
                onClick={() => { setVerordnungEditId(null); setVerordnungenOpen(true); }}
                style={{ background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '6px 11px', color: tc.text, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Outfit,sans-serif' }}
              >{t('pflege.verordnungen.title')}</button>
            )}
            <button
              data-testid="jahresuebersicht-add-absence"
              onClick={() => setAbsenceDraft('new')}
              style={{ background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '6px 11px', color: tc.text, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Outfit,sans-serif' }}
            >+ {t(isBk ? 'betreuung.availability.add' : 'pflege.absences.add')}</button>
            <button
              onClick={() => setCareCreateOpen(true)}
              style={{ background: BRAND_LIGHT, border: `1px solid ${BRAND}`, borderRadius: 7, padding: '6px 11px', color: BRAND, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Outfit,sans-serif' }}
            >+ {t('pflege.cares.create')}</button>
          </div>
        </div>
        {collapsed ? null : caresLoading ? (
          <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tc.muted, fontSize: 13 }}>
            {t('common.loading_short')}
          </div>
        ) : (
          <CareYearTimeline
            cares={cares}
            year={year}
            onYearChange={setYear}
            dark={dark}
            userRole={user.user_role ?? 2}
            emptyLabel={t('pflege.cares.timeline_empty')}
            absences={absences}
            tasks={tasks as unknown as TimelineTask[]}
            verordnungen={verordnungen}
            onCareClick={care => setEditCare(care)}
            onAbsenceClick={id => { const a = absences.find(x => x.id === id); if (a) setAbsenceDraft(a); }}
            onTaskClick={id => { const tk = tasks.find(x => x.id === id); if (tk) setEditTask(tk); }}
            onVerordnungClick={id => { setVerordnungEditId(id); setVerordnungenOpen(true); }}
          />
        )}
      </div>

      {/* ── Betreuung bearbeiten ──────────────────────────────────────── */}
      <EditDrawer
        open={editCare !== null}
        onClose={() => setEditCare(null)}
        title={t('pflege.detail.dashboard.betreuungen')}
        dark={dark}
      >
        {editCare && (
          <CareEditForm
            dark={dark}
            care={{ ...editCare, pb_user_id: editCare.pb_user_id ?? user.id }}
            // Nur im PB-Profil: dort ist `user` der PB, dessen Startdatum sich ändert.
            onPbStartdatumChanged={isBk ? undefined : onPbStartdatumChanged}
            onSaved={() => { setEditCare(null); loadCares(); onChanged?.(); }}
            onCancel={() => setEditCare(null)}
          />
        )}
      </EditDrawer>

      {/* ── Abwesenheit anlegen/bearbeiten ────────────────────────────── */}
      <EditDrawer
        open={absenceDraft !== null}
        onClose={() => setAbsenceDraft(null)}
        title={isBk
          ? (absenceDraft && absenceDraft !== 'new' ? t('betreuung.availability.edit_title') : t('betreuung.availability.add'))
          : (absenceDraft && absenceDraft !== 'new' ? t('pflege.absences.edit_title') : t('pflege.absences.add'))}
        dark={dark}
      >
        {absenceDraft !== null && (
          <AbsenceForm
            dark={dark}
            variant={isBk ? 'bk' : 'pb'}
            userId={user.id}
            initial={absenceDraft === 'new' ? null : absenceDraft}
            onSaved={() => { setAbsenceDraft(null); loadAbsences(); onChanged?.(); }}
            onCancel={() => setAbsenceDraft(null)}
          />
        )}
      </EditDrawer>

      {/* ── Aufgabe bearbeiten (Marker-Klick) ─────────────────────────── */}
      <EditDrawer
        open={editTask !== null}
        onClose={() => setEditTask(null)}
        title={t('pflege.detail.dashboard.aufgaben')}
        dark={dark}
      >
        {editTask && (
          <TaskEditForm
            dark={dark}
            task={editTask}
            userId={user.id}
            admins={admins}
            onSaved={() => { setEditTask(null); loadTasks(); onTasksChanged?.(); }}
            onCancel={() => setEditTask(null)}
          />
        )}
      </EditDrawer>

      {/* ── Verordnungsmanagement ─────────────────────────────────────── */}
      <EditDrawer
        open={verordnungenOpen}
        onClose={() => setVerordnungenOpen(false)}
        title={t('pflege.verordnungen.title')}
        dark={dark}
      >
        {verordnungenOpen && (
          <VerordnungenPanel
            key={`verordnungen-${verordnungEditId ?? 'list'}`}
            dark={dark}
            userId={user.id}
            verordnungen={verordnungen}
            editId={verordnungEditId}
            onChanged={() => { loadVerordnungen(); onChanged?.(); }}
          />
        )}
      </EditDrawer>

      {/* ── Neue Betreuung anlegen ────────────────────────────────────── */}
      {careCreateOpen && (
        <CreateCareModal
          dark={dark}
          pbUserId={user.id}
          // Im BK-Profil ist die BK fix und der PB wird gewählt.
          userRole={user.user_role ?? 2}
          onClose={() => setCareCreateOpen(false)}
          onCreated={() => { setCareCreateOpen(false); loadCares(); onChanged?.(); }}
          onPbStartdatumChanged={onPbStartdatumChanged}
        />
      )}
    </>
  );
}
